// apps/web/src/features/agent/artifacts/KpiArtifactCard.tsx (#1877)
//
// Renders a `kpi` artifact: a title plus its KPI items as compact cards.
// Mirrors the dashboard KPI token style (`features/dashboards/china-requests`)
// without importing across features. Values render verbatim — never
// recomputed or reformatted into different numbers.
//
// INVARIANT (XSS): every model-supplied string (title, label, unit) renders
// only inside JSX text nodes. No `dangerouslySetInnerHTML`, no HTML/markdown
// parsing — see the feature-level XSS invariant in ./ArtifactRenderer.tsx.
import type { CSSProperties } from 'react';
import type { Artifact } from '@magimp/shared';

type KpiArtifact = Extract<Artifact, { kind: 'kpi' }>;

const SECTION_CARD: CSSProperties = {
  background: 'var(--bg-1)',
  border: '1px solid var(--border-1)',
  borderRadius: 'var(--r-2)',
  padding: 12,
};

// Direction tint mirrors the dashboard KPI delta: positive → success,
// negative → error, zero/neutral → muted foreground.
function deltaColor(delta: number): string {
  if (delta > 0) return 'var(--status-success-fg)';
  if (delta < 0) return 'var(--status-error-fg)';
  return 'var(--fg-2)';
}

function arrow(delta: number): string {
  if (delta > 0) return '▲';
  if (delta < 0) return '▼';
  return '';
}

export function KpiArtifactCard({ artifact }: { artifact: KpiArtifact }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-2)' }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-1)' }}>{artifact.title}</span>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 12,
        }}
      >
        {artifact.items.map((item, i) => (
          <div key={i} style={SECTION_CARD}>
            <div style={{ fontSize: 12, color: 'var(--fg-2)' }}>{item.label}</div>
            <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>
              {item.value}
              {item.unit !== undefined ? (
                <span style={{ fontSize: 13, color: 'var(--fg-2)', marginLeft: 2 }}>
                  {item.unit}
                </span>
              ) : null}
            </div>
            {item.delta !== undefined ? (
              <div style={{ fontSize: 12, marginTop: 4, color: deltaColor(item.delta) }}>
                {arrow(item.delta)} {item.delta}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
